import appSettings from "@/appsettings.json";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { ProjectSubmission } from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Group,
	Pagination,
	ScrollArea,
	Skeleton,
	Table,
	Text,
	Tooltip,
	useMantineTheme
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
	IconAlertCircle,
	IconDownload,
	IconExclamationMark,
	IconExternalLink,
	IconFileMusic,
	IconFileUpload,
	IconLoader,
	IconMathGreater,
	IconPencilCheck,
	IconRefresh
} from "@tabler/icons-react";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

/* ----------------------------- constants/helpers ---------------------------- */

const HEADER_FW = 700;

const CW = {
	desktop: {
		parent: {
			submitted: "16%",
			editorDue: "16%",
			instrumentalistDue: "16%",
			uploadVideo: "16%",
			approve: "16%",
			expand: "8%",
			projectOwner: "16%",
			projectName: "16%",
		},
		child: {
			indent: "18%",
			instrumentName: "30%",
			sheetMusic: "20%",
			backing: "20%",
			rightIndent: "12%",
		},
	},
	mobile: {
		parent: {
			submitted: "18%",
			editorDue: "18%",
			instrumentalistDue: "18%",
			names: "22%",
			uploadVideo: "14%",
			approve: "14%",
			expand: "10%",
		},
		child: {
			indent: "0%",
			instrumentName: "40%",
			sheetMusic: "25%",
			backing: "25%",
			rightIndent: "10%",
		},
	},
} as const;

const truncate = (s: string, max = 10) =>
	(s?.length ?? 0) > max ? s.slice(0, max) + "..." : s ?? "";

// Defensive date parsing
const asDate = (d: Date | string | null | undefined): Date | null => {
	if (!d) return null;
	if (d instanceof Date) return isNaN(+d) ? null : d;
	const iso = /Z$/.test(d) ? d : `${d}Z`;
	const parsed = new Date(iso);
	return isNaN(+parsed) ? null : parsed;
};

const toLocal = (d: Date | string | null | undefined): string =>
	asDate(d)?.toLocaleString() ?? "—";

/* ------------------------------- upload states ------------------------------ */

type UploadState =
	| {
		key: "processing" | "completed" | "submitAgain" | "pastDue" | "notSubmitted";
		label: string;
		icon: React.ReactNode;
		loading: boolean;
		disabled: boolean;
		tooltip?: string;
	}
	| {
		key: "error";
		label: string;
		icon: React.ReactNode;
		loading: boolean;
		disabled: boolean;
		tooltip: string;
	};

const computeUploadState = (row: ProjectSubmission, fzMed: number): UploadState => {
	if (row.submissionStatus === "Uploading") {
		return {
			key: "processing",
			label: "Processing",
			icon: <IconLoader size={fzMed} />,
			loading: true,
			disabled: true,
		};
	}

	const editorDue = asDate(row.editorDueDate);
	const now = new Date();

	if (row.submissionStatus === "Error") {
		return {
			key: "error",
			label: "Not Yet Submitted",
			icon: <IconAlertCircle size={fzMed} />,
			loading: false,
			disabled: false,
			tooltip:
				"An error occurred while uploading your video. Please try again. If the problem persists, contact support.",
		};
	}

	if (row.dateFulfilled) {
		if (editorDue && editorDue < now) {
			return {
				key: "completed",
				label: "Upload Complete",
				icon: <IconFileMusic size={fzMed} />,
				loading: false,
				disabled: false,
			};
		}
		return {
			key: "submitAgain",
			label: "Submit Again",
			icon: <IconFileMusic size={fzMed} />,
			loading: false,
			disabled: false,
		};
	}

	if (editorDue && editorDue < now) {
		return {
			key: "pastDue",
			label: "Past Due",
			icon: <IconExclamationMark size={fzMed} />,
			loading: false,
			disabled: false,
		};
	}

	return {
		key: "notSubmitted",
		label: "Not Yet Submitted",
		icon: <IconFileUpload size={fzMed} />,
		loading: false,
		disabled: false,
	};
};

/* -------------------------------- shared buttons ---------------------------- */

const UploadButton: React.FC<{
	row: ProjectSubmission;
	fzSmall: number;
	fzMed: number;
	isUploading: boolean;
	onPickFile: () => void;
}> = ({ row, fzSmall, fzMed, isUploading, onPickFile }) => {
	const theme = useMantineTheme();
	const st = computeUploadState(row, fzMed);
	const disabled =
		isUploading || st.disabled || row.submissionStatus === "Uploading";

	const button = (
		<Tooltip
			disabled={row.submissionStatus !== "Uploading" && !isUploading}
			label={
				row.submissionStatus === "Uploading"
					? "This video is being processed."
					: isUploading
						? "Uploading..."
						: undefined
			}
			position="top"
			withArrow
		>
			<Button
				variant="default"
				size="compact-sm"
				fz={fzSmall}
				c={theme.colors.blue[8]}
				loading={st.loading || disabled}
				loaderProps={{ type: "bars" }}
				onClick={(e) => {
					if (disabled) return;
					onPickFile();
					e.stopPropagation();
				}}
				leftSection={st.icon}
			>
				{st.label}
			</Button>
		</Tooltip>
	);

	if (st.key === "error" && st.tooltip) {
		return (
			<Tooltip label={st.tooltip} position="top" withArrow>
				{button}
			</Tooltip>
		);
	}
	return button;
};

const BackingTrackButton: React.FC<{
	submission: ProjectSubmission;
	fzMed: number;
}> = ({ submission, fzMed }) => {
	const theme = useMantineTheme();
	const onClick = () => {
		const url = `${appSettings.S3BucketBaseUrl}InstrumentalistTaskFiles/${submission.id}.mp3`;
		const file = `${submission.submissionProjectName}-BackingTrack.mp3`.replace(
			/\s+/g,
			"_"
		);
		Utils.downloadBlob(url, file, <IconDownload />, <IconAlertCircle />);
	};

	return (
		<Button
			variant="default"
			size="compact-sm"
			c={theme.colors.blue[8]}
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
		>
			<IconFileMusic size={fzMed} />
			<IconDownload size={fzMed} />
		</Button>
	);
};

const SheetMusicButton: React.FC<{
	submission: ProjectSubmission;
	taskId: string;
	instrumentName: string;
	fzMed: number;
}> = ({ submission, taskId, instrumentName, fzMed }) => {
	const theme = useMantineTheme();
	const onClick = () => {
		const url = `${appSettings.S3BucketBaseUrl}InstrumentalistTaskFiles/${taskId}.pdf`;
		const file = `${submission.submissionProjectName}-${instrumentName}-SheetMusic.pdf`.replace(
			/\s+/g,
			"_"
		);
		Utils.downloadBlob(url, file, <IconDownload />, <IconAlertCircle />);
	};

	return (
		<Button
			variant="default"
			size="compact-sm"
			c={theme.colors.blue[8]}
			onClick={onClick}
		>
			<IconFileMusic size={fzMed} />
			<IconDownload size={fzMed} />
		</Button>
	);
};

/* ---------------------------- simplified task table ------------------------- */

const SubmissionTasksTable: React.FC<{
	submission: ProjectSubmission;
	isMobile: boolean;
	fzSmall: number;
	fzMed: number;
}> = ({ submission, isMobile, fzSmall, fzMed }) => {
	return (
		<Box style={{ overflowX: "auto", width: "100%" }}>
			<Table w="100%">
				<Table.Thead>
					<Table.Tr>
						<Table.Th
							w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
						/>
						<Table.Th
							w={
								isMobile
									? CW.mobile.child.instrumentName
									: CW.desktop.child.instrumentName
							}
							ta="center"
						>
							<Text fz={fzSmall} fw={HEADER_FW}>
								Instrument
							</Text>
						</Table.Th>
						<Table.Th
							w={isMobile ? CW.mobile.child.sheetMusic : CW.desktop.child.sheetMusic}
							ta="center"
						>
							<Text fz={fzSmall} fw={HEADER_FW}>
								Sheet Music
							</Text>
						</Table.Th>
						<Table.Th
							w={isMobile ? CW.mobile.child.backing : CW.desktop.child.backing}
							ta="center"
						>
							<Text fz={fzSmall} fw={HEADER_FW}>
								Backing Track
							</Text>
						</Table.Th>
						<Table.Th
							w={
								isMobile
									? CW.mobile.child.rightIndent
									: CW.desktop.child.rightIndent
							}
						/>
					</Table.Tr>
				</Table.Thead>
				<Table.Tbody>
					{submission.instrumentalistTasks.map((t) => {
						const isFeedback = submission.feedbackInstrumentalistTaskId === t.id;
						return (
							<Table.Tr key={t.id}>
								<Table.Td
									w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
								/>
								<Table.Td
									w={
										isMobile
											? CW.mobile.child.instrumentName
											: CW.desktop.child.instrumentName
									}
									ta="center"
									fz={fzSmall}
								>
									<Group gap={6} justify="center">
										<Text fz={fzSmall}>
											{isMobile ? truncate(t.instrument.name) : t.instrument.name}
										</Text>
										{isFeedback && (
											<Badge color="yellow" variant="filled" size="xs">
												Feedback Provider
											</Badge>
										)}
									</Group>
								</Table.Td>
								<Table.Td
									w={
										isMobile
											? CW.mobile.child.sheetMusic
											: CW.desktop.child.sheetMusic
									}
									ta="center"
								>
									<SheetMusicButton
										submission={submission}
										taskId={t.id}
										instrumentName={t.instrument.name}
										fzMed={fzMed}
									/>
								</Table.Td>
								<Table.Td
									w={isMobile ? CW.mobile.child.backing : CW.desktop.child.backing}
									ta="center"
								>
									<BackingTrackButton submission={submission} fzMed={fzMed} />
								</Table.Td>
								<Table.Td
									w={
										isMobile
											? CW.mobile.child.rightIndent
											: CW.desktop.child.rightIndent
									}
								/>
							</Table.Tr>
						);
					})}
				</Table.Tbody>
			</Table>
		</Box>
	);
};

/* ----------------------------- submission row -------------------------------- */

type SubmissionRowProps = {
	row: ProjectSubmission;
	isMobile: boolean;
	fzSmall: number;
	fzMed: number;
	isExpanded: boolean;
	onToggle: () => void;
	isUploading: boolean;
	isGenerating: boolean;
	onPickFile: () => void;
	onGenerate: () => void;
	onApprove: () => void;
};

const SubmissionRow: React.FC<SubmissionRowProps> = ({
	row,
	isMobile,
	fzSmall,
	fzMed,
	isExpanded,
	onToggle,
	isUploading,
	isGenerating,
	onPickFile,
	onGenerate,
	onApprove,
}) => {
	const theme = useMantineTheme();
	const hasTasks = row.instrumentalistTasks.length > 0;

	return (
		<>
			<Table.Tr
				onClick={() => hasTasks && onToggle()}
				bg={theme.colors.blue[1]}
				style={{ cursor: hasTasks ? "pointer" : "auto" }}
				key={row.id}
			>
				{/* Submitted */}
				<Table.Td
					fz={fzSmall}
					w={isMobile ? CW.mobile.parent.submitted : CW.desktop.parent.submitted}
					ta="center"
				>
					{toLocal(row.dateSubmitted)}
				</Table.Td>

				{/* Editor Due */}
				<Table.Td
					fz={fzSmall}
					w={isMobile ? CW.mobile.parent.editorDue : CW.desktop.parent.editorDue}
					ta="center"
				>
					{toLocal(row.editorDueDate)}
				</Table.Td>

				{/* Instrumentalist Due */}
				<Table.Td
					fz={fzSmall}
					w={
						isMobile
							? CW.mobile.parent.instrumentalistDue
							: CW.desktop.parent.instrumentalistDue
					}
					ta="center"
				>
					{toLocal(row.instrumentalistDueDate)}
				</Table.Td>

				{/* Names */}
				{isMobile ? (
					<Table.Td
						fz={fzSmall}
						w={CW.mobile.parent.names}
						ta="center"
					>
						<Group>
							{`${truncate(row.submissionProjectName || "N/A Project")} by ${truncate(
								row.projectOwner?.username ?? "N/A"
							)}`}
							<ActionIcon
								size={fzSmall + 4}
								onClick={() => Utils.openProject(row.projectId)}
							>
								<IconExternalLink
									size={fzMed}
								/>
							</ActionIcon>
						</Group>
					</Table.Td>
				) : (
					<>
						<Table.Td
							fz={fzSmall}
							w={CW.desktop.parent.projectOwner}
							ta="center"
						>
							{row.projectOwner?.username || "N/A User"}
						</Table.Td>
						<Table.Td
							fz={fzSmall}
							w={CW.desktop.parent.projectName}
							ta="center"
						>
							<Group>
								{row.submissionProjectName || "N/A Project"}
								<ActionIcon
									size={fzSmall + 4}
									onClick={() => Utils.openProject(row.projectId)}
								>
									<IconExternalLink
										size={fzMed}
									/>
								</ActionIcon>
							</Group>
						</Table.Td>
					</>
				)}

				{/* Submit Directly */}
				<Table.Td
					fz={fzSmall}
					w={
						isMobile
							? CW.mobile.parent.uploadVideo
							: CW.desktop.parent.uploadVideo
					}
					ta="center"
				>
					<UploadButton
						row={row}
						fzSmall={fzSmall}
						fzMed={fzMed}
						isUploading={isUploading}
						onPickFile={onPickFile}
					/>
				</Table.Td>

				{/* Generate/Approve */}
				<Table.Td
					fz={fzSmall}
					w={isMobile ? CW.mobile.parent.approve : CW.desktop.parent.approve}
					ta="center"
				>
					<Button
						variant="default"
						size="compact-sm"
						fz={fzSmall}
						c={theme.colors.blue[8]}
						loading={isGenerating}
						loaderProps={{ variant: "bars" }}
						onClick={(e) => {
							e.stopPropagation();
							if (hasTasks) onApprove();
							else onGenerate();
						}}
					>
						<Text>{hasTasks ? "Approve" : "Generate Tasks"}</Text>
					</Button>
				</Table.Td>

				{/* Expand chevron */}
				<Table.Td
					w={isMobile ? CW.mobile.parent.expand : CW.desktop.parent.expand}
					ta="center"
				>
					{hasTasks ? (
						<Box ml={isMobile ? -7 : 0}>
							<IconMathGreater
								size={fzMed}
								style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
							/>
						</Box>
					) : null}
				</Table.Td>
			</Table.Tr>

			{isExpanded && hasTasks && (
				<Table.Tr key={`${row.id}-expanded`}>
					<Table.Td colSpan={isMobile ? 7 : 8} p={0}>
						<SubmissionTasksTable
							submission={row}
							isMobile={isMobile}
							fzSmall={fzSmall}
							fzMed={fzMed}
						/>
					</Table.Td>
				</Table.Tr>
			)}
		</>
	);
};

/* --------------------------------- main comp -------------------------------- */

const SubmissionReview: React.FC = () => {
	const [submissions, setSubmissions] = useState<
		ProjectSubmission[] | undefined
	>(undefined);
	const [expanded, setExpanded] = useState<string[]>([]);
	const [uploadingIds, setUploadingIds] = useState<string[]>([]);
	const [isGeneratingTasks, setIsGeneratingTasks] = useState(false);

	const [page, setPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(true);

	const theme = useMantineTheme();
	const isMobile = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzSmall = isMobile ? 10 : 14;
	const fzMed = isMobile ? 14 : 18;

	const session = useContext(SessionContext);
	if (!session) return null;

	const FILTERS = {
		approvedOnly: false,
		unapprovedOnly: true,
		fulfilledOnly: false,
		associatedUserId: undefined as string | undefined,
		fulfilledTasksOnly: false,
		unfulfilledTasksOnly: false,
		classroomId: undefined as string | undefined,
	};

	const parentColCount = isMobile ? 7 : 8;

	const skeletonRows = useMemo(() => {
		const cell = <Skeleton height={20} ta="center" />;
		const cols = parentColCount;
		const row = (
			<Table.Tr bg={theme.colors.blue[1]}>
				{Array.from({ length: cols }).map((_, i) => (
					<Table.Td key={i} fz={fzSmall} w="100%" ta="center">
						{cell}
					</Table.Td>
				))}
			</Table.Tr>
		);
		return Array.from({ length: 4 }, (_, i) =>
			React.cloneElement(row, { key: `s${i}` })
		);
	}, [parentColCount, theme.colors.blue, fzSmall]);

	// fetch submissions
	const fetchPage = useCallback(
		async (p: number) => {
			if (session.user == null || session.token == null) return;
			try {
				setLoading(true);
				setSubmissions(undefined);

				const res = await session.getProjectSubmissionsPage(
					p,
					FILTERS.approvedOnly,
					FILTERS.unapprovedOnly,
					FILTERS.fulfilledOnly,
					FILTERS.associatedUserId,
					FILTERS.fulfilledTasksOnly,
					FILTERS.unfulfilledTasksOnly,
					FILTERS.classroomId
				);

				setSubmissions(res.items);
				setTotalPages(res.totalPages);
				setExpanded(res.items.map((s) => s.id)); // expand all by default
			} catch (err) {
				console.error("Error fetching submissions:", err);
				Utils.displayNotification(
					"Project Submissions Load Failure",
					"Something went wrong when loading project submissions. Please try again later.",
					"red",
					<IconAlertCircle size={16} />,
					true,
					false
				);
				setSubmissions([]);
				setTotalPages(1);
				setExpanded([]);
			} finally {
				setLoading(false);
			}
		},
		[session]
	);

	useEffect(() => {
		void fetchPage(page);
	}, [page, fetchPage]);

	const handleRefresh = useCallback(async () => {
		setSubmissions(undefined);
		session.clearProjectSubmissionsCache();
		if (page === 1) {
			fetchPage(1);
		} else {
			setPage(1);
		}
	}, [session, page, fetchPage]);

	// Upload file (same as before)
	const uploadFile = useCallback(
		async (file: File, submissionID: string) => {
			const token = session.token;
			if (!token) return;

			setUploadingIds((prev) => [...prev, submissionID]);
			const shouldContinue = await Utils.confirmLargeUpload(
				file,
				200 * 1024 * 1024
			); // 200MB
			if (!shouldContinue) {
				setUploadingIds((prev) => prev.filter((x) => x !== submissionID));
				return;
			}

			const totalBytes = file.size;
			const controller = new AbortController();

			const beforeUnload = (e: BeforeUnloadEvent) => {
				e.preventDefault();
				e.returnValue = "";
			};

			const renderProgress = (percent: number, onCancel: () => void) => (
				<div style={{ width: "100%" }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							marginBottom: 6,
						}}
					>
						<span>{percent.toFixed(0)}%</span>
						<button
							type="button"
							onClick={onCancel}
							style={{
								border: "none",
								background: "transparent",
								color: "#fa5252",
								cursor: "pointer",
								fontSize: 12,
							}}
							aria-label="Cancel upload"
						>
							Cancel upload
						</button>
					</div>
					<div
						style={{
							height: 8,
							background: "rgba(0,0,0,0.1)",
							borderRadius: 4,
							overflow: "hidden",
						}}
					>
						<div
							style={{
								height: "100%",
								width: `${Math.max(0, Math.min(100, percent))}%`,
								background: "#228be6",
								transition: "width 150ms linear",
							}}
						/>
					</div>
				</div>
			);

			const notificationId = Utils.displayNotification(
				"Uploading video... Please do not close this page or navigate away.",
				"Starting upload...",
				"blue",
				"loading",
				false,
				false
			);

			window.addEventListener("beforeunload", beforeUnload);

			let lastUpdate = 0;
			const onProgress = (uploadedBytes: number) => {
				const pct = (uploadedBytes / totalBytes) * 100;
				const now = performance.now();
				if (now - lastUpdate < 300 && pct < 100) return;
				lastUpdate = now;

				if (pct < 100) {
					Utils.updateNotification(
						notificationId,
						"Uploading video... Please do not close this page or navigate away.",
						renderProgress(pct, () => controller.abort()),
						"blue",
						"loading",
						false,
						false
					);
				} else {
					Utils.updateNotification(
						notificationId,
						"Upload complete! We are now processing the video, which may take a few minutes.",
						"You may navigate away from this page and return later to check the results.",
						"green",
						<IconFileUpload size={fzMed} />,
						true,
						false
					);

					setTimeout(handleRefresh, 5000);
				}
			};

			try {
				await ConductionAPI.fulfillProjectSubmission(
					submissionID,
					file,
					token,
					{
						onProgress,
						signal: controller.signal,
					}
				);

				session.clearProjectSubmissionsCache();
				void fetchPage(page);
			} catch (error) {
				const isAbort =
					error instanceof DOMException && error.name === "AbortError";
				const msg = error instanceof Error ? error.message : "Unknown error";

				if (isAbort) {
					Utils.updateNotification(
						notificationId,
						"Upload canceled",
						undefined,
						"yellow",
						<IconAlertCircle size={fzMed} />,
						true,
						8000
					);
				} else {
					Utils.updateNotification(
						notificationId,
						"Error: Video Not Submitted",
						msg ||
						"Try reducing the file size (MP4 or lower resolution) and try again.",
						"red",
						<IconAlertCircle size={fzMed} />,
						true,
						false
					);

					setTimeout(handleRefresh, 5000);
				}
			} finally {
				setUploadingIds((prev) => prev.filter((x) => x !== submissionID));
				window.removeEventListener("beforeunload", beforeUnload);
			}
		},
		[session, fzMed, fetchPage, page, handleRefresh]
	);

	const createFilePicker = useCallback(
		(submissionID: string) => {
			if (uploadingIds.includes(submissionID)) return;
			Utils.createFilePicker((file) => {
				void uploadFile(file, submissionID);
			});
		},
		[uploadingIds, uploadFile]
	);

	// Generate / Approve
	const handleGenerateTasks = useCallback(
		async (submissionID: string) => {
			if (!session.token) {
				Utils.displayNotification(
					"Error",
					"You must be logged in to generate tasks.",
					"red",
					<IconAlertCircle size={fzMed} />
				);
				return;
			}

			const notificationId = Utils.displayNotification(
				"Generating tasks...",
				null,
				"blue",
				"loading"
			);
			setIsGeneratingTasks(true);

			try {
				await ConductionAPI.generateProjectSubmissionTasks(
					submissionID,
					session.token
				);
				Utils.updateNotification(
					notificationId,
					"Tasks generated successfully",
					"Tasks have been generated for the submission.",
					"green",
					<IconPencilCheck size={fzMed} />
				);
				session.clearProjectSubmissionsCache();
				void fetchPage(page);
			} catch (error) {
				console.error("Error generating tasks:", error);
				Utils.updateNotification(
					notificationId,
					"Error generating tasks",
					"An error occurred while generating tasks. Please try again later.",
					"red",
					<IconAlertCircle size={fzMed} />
				);
			} finally {
				setIsGeneratingTasks(false);
			}
		},
		[session, fzMed, fetchPage, page]
	);

	const handleApproveTasks = useCallback(
		async (submissionID: string) => {
			if (!session.token) {
				Utils.displayNotification(
					"Error",
					"You must be logged in to approve tasks.",
					"red",
					<IconAlertCircle size={fzMed} />
				);
				return;
			}

			const notificationId = Utils.displayNotification(
				"Approving tasks...",
				null,
				"blue",
				"loading"
			);
			setIsGeneratingTasks(true);

			try {
				await ConductionAPI.approveProjectSubmission(
					submissionID,
					session.token
				);
				Utils.updateNotification(
					notificationId,
					"Tasks approved successfully",
					null,
					"green",
					<IconPencilCheck size={fzMed} />
				);
				session.clearProjectSubmissionsCache();
				void fetchPage(page);
			} catch (error) {
				console.error("Error approving tasks:", error);
				Utils.updateNotification(
					notificationId,
					"Error approving tasks",
					null,
					"red",
					<IconAlertCircle size={fzMed} />
				);
			} finally {
				setIsGeneratingTasks(false);
			}
		},
		[session, fzMed, fetchPage, page]
	);

	const rows = useMemo(() => {
		if (!submissions) return null;
		return submissions.map((row) => {
			const isExpanded = expanded.includes(row.id);
			return (
				<SubmissionRow
					key={row.id}
					row={row}
					isMobile={isMobile}
					fzSmall={fzSmall}
					fzMed={fzMed}
					isExpanded={isExpanded}
					onToggle={() =>
						setExpanded((prev) =>
							prev.includes(row.id)
								? prev.filter((x) => x !== row.id)
								: [...prev, row.id]
						)
					}
					isUploading={uploadingIds.includes(row.id)}
					isGenerating={isGeneratingTasks}
					onPickFile={() => createFilePicker(row.id)}
					onGenerate={() => handleGenerateTasks(row.id)}
					onApprove={() => handleApproveTasks(row.id)}
				/>
			);
		});
	}, [
		submissions,
		expanded,
		isMobile,
		fzSmall,
		fzMed,
		uploadingIds,
		isGeneratingTasks,
		createFilePicker,
		handleGenerateTasks,
		handleApproveTasks,
	]);

	return (
		<Box
			w="100%"
			h="100%"
			style={{
				display: "grid",
				gridTemplateRows: "auto 1fr auto",
				overflow: "hidden",
				minHeight: 0,
			}}
		>
			{/* Header */}
			<Group w="100%" justify="space-between" align="center" p="xs">
				<Text c={theme.colors.blue[0]}>
					Review unapproved project submissions, generate/approve tasks, upload
					videos.
				</Text>
				<Group gap="xs" align="center">
					<Button
						leftSection={<IconRefresh size={fzMed} />}
						onClick={handleRefresh}
						c={theme.colors.blue[0]}
					>
						Refresh
					</Button>
				</Group>
			</Group>

			{/* Table */}
			<ScrollArea
				h="100%"
				w="100%"
				bg={theme.colors.blue[0]}
				type="auto"
				viewportProps={{ style: { height: "100%" } }}
				styles={{ viewport: { overflowX: "hidden" } }}
			>
				<Table
					horizontalSpacing="md"
					verticalSpacing="xs"
					miw="auto"
					layout="fixed"
					style={{ tableLayout: "fixed" }}
					stickyHeader
				>
					<Table.Thead>
						<Table.Tr>
							<Table.Th
								w={isMobile ? CW.mobile.parent.submitted : CW.desktop.parent.submitted}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW} ta="center">
									Submitted Date
								</Text>
							</Table.Th>
							<Table.Th
								w={isMobile ? CW.mobile.parent.editorDue : CW.desktop.parent.editorDue}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW} ta="center">
									Editor Due Date
								</Text>
							</Table.Th>
							<Table.Th
								w={
									isMobile
										? CW.mobile.parent.instrumentalistDue
										: CW.desktop.parent.instrumentalistDue
								}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW} ta="center">
									Instrumentalist Due Date
								</Text>
							</Table.Th>

							{isMobile ? (
								<Table.Th w={CW.mobile.parent.names} ta="center">
									<Text fz={fzSmall} fw={HEADER_FW}>
										Info
									</Text>
								</Table.Th>
							) : (
								<>
									<Table.Th w={CW.desktop.parent.projectOwner} ta="center">
										<Text fz={fzSmall} fw={HEADER_FW}>
											User Name
										</Text>
									</Table.Th>
									<Table.Th w={CW.desktop.parent.projectName} ta="center">
										<Text fz={fzSmall} fw={HEADER_FW}>
											Project Name
										</Text>
									</Table.Th>
								</>
							)}

							<Table.Th
								w={isMobile ? CW.mobile.parent.uploadVideo : CW.desktop.parent.uploadVideo}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW}>
									{isMobile ? "Submit Directly" : "Submit Video Directly"}
								</Text>
							</Table.Th>
							<Table.Th
								w={isMobile ? CW.mobile.parent.approve : CW.desktop.parent.approve}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW}>
									Generate / Approve
								</Text>
							</Table.Th>
							<Table.Th
								w={isMobile ? CW.mobile.parent.expand : CW.desktop.parent.expand}
								ta="center"
							>
								<Text fz={fzSmall} fw={500} />
							</Table.Th>
						</Table.Tr>
					</Table.Thead>

					<Table.Tbody>
						{loading ? (
							skeletonRows
						) : rows && rows.length > 0 ? (
							rows
						) : (
							<Table.Tr>
								<Table.Td colSpan={isMobile ? 7 : 8}>
									<Text fw={500} ta="center">
										No Project Submissions
									</Text>
								</Table.Td>
							</Table.Tr>
						)}
					</Table.Tbody>
				</Table>
			</ScrollArea>

			{/* Pagination */}
			{totalPages > 1 ? (
				<Group w="100%" justify="flex-end" align="center" p="xs">
					<Pagination
						total={totalPages}
						value={page}
						onChange={setPage}
						size="sm"
						withEdges
						disabled={loading}
					/>
				</Group>
			) : (
				<Box style={{ height: 0 }} />
			)}
		</Box>
	);
};

export default SubmissionReview;
